/**
 * 图片上传前自动压缩 —— 参考《上传优化方案.md》§4.4
 * 规则：jpg/png/webp，若 宽高≤1600 且 <500KB 原样返回；
 * 否则等比缩放到 ≤1600px → WebP(0.85)；不支持 WebP 编码回退 JPEG(0.9)；变小才采用。
 */
export async function maybeCompressImage(file: File): Promise<{ file: File; compressed: boolean }> {
  if (!/^image\/(jpeg|png|webp)$/i.test(file.type)) return { file, compressed: false }

  const loadImage = () =>
    new Promise<HTMLImageElement>((resolve, reject) => {
      const url = URL.createObjectURL(file)
      const img = new Image()
      img.onload = () => { URL.revokeObjectURL(url); resolve(img) }
      img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('图片解码失败')) }
      img.src = url
    })

  const img = await loadImage().catch(() => null)
  if (!img) return { file, compressed: false }

  const MAX = 1600
  const w = img.naturalWidth
  const h = img.naturalHeight
  if ((w <= MAX && h <= MAX) && file.size < 500 * 1024) return { file, compressed: false }

  const scale = Math.min(1, MAX / Math.max(w, h))
  const nw = Math.max(1, Math.round(w * scale))
  const nh = Math.max(1, Math.round(h * scale))
  const canvas = document.createElement('canvas')
  canvas.width = nw
  canvas.height = nh
  const ctx = canvas.getContext('2d')
  if (!ctx) return { file, compressed: false }
  ctx.drawImage(img, 0, 0, nw, nh)

  const toBlob = (type: string, q: number) =>
    new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, q))

  let blob: Blob | null = await toBlob('image/webp', 0.85)
  let type = 'image/webp'
  if (!blob) {
    blob = await toBlob('image/jpeg', 0.9)
    type = 'image/jpeg'
  }
  if (!blob || blob.size >= file.size) return { file, compressed: false } // 没变小就不换

  const ext = type === 'image/webp' ? 'webp' : 'jpg'
  const name = file.name.replace(/\.(jpe?g|png|webp)$/i, '') + '.' + ext
  return { file: new File([blob], name, { type }), compressed: true }
}

/** 人类可读大小：MB/KB */
export function fmtBytes(n: number): string {
  if (n >= 1048576) return (n / 1048576).toFixed(1) + ' MB'
  if (n >= 1024) return Math.round(n / 1024) + ' KB'
  return n + ' B'
}
